import { Injectable } from "@angular/core";
import { v4 as uuidv4 } from "uuid";
import { Assertion, AssertionIssue, Media, SyncableStatus } from "../../../core/database/db";
import { AssertionChanges, BqInspectionDataService } from "./bq-inspection-data.service";

/** The three answers a CM can give a BQ2/BQ3/BQ4 checklist item. */
export enum ChecklistResponse {
  Pass = "pass",
  NeedsAttention = "needsAttention",
  NotApplicable = "notApplicable"
}

/** Photos and notes captured by the Needs Attention sheet. */
export interface ChecklistIssueDetails {
  notes: string;
  media: Media[];
}

const UNSET_SEVERITY = "unset";

@Injectable()
export class BqInspectionSingleAreaDataService extends BqInspectionDataService {
  /**
   * Records a BQ2/BQ3/BQ4 checklist answer against an assertion. Pass and N/A live on the assertion
   * itself; Needs Attention creates an assertion issue from the photos and notes captured by the sheet.
   * Every write goes to IndexedDB as Pending so it survives offline and syncs with the rest of the
   * assertion data.
   */
  async recordChecklistResponse(assertion: Assertion, response: ChecklistResponse, lotAreaIdentifier: string, issueDetails?: ChecklistIssueDetails): Promise<void> {
    await this.startJob();

    if (response === ChecklistResponse.NeedsAttention) {
      const now = new Date();
      const issue: AssertionIssue = {
        // JobIssue.IssueText is NOT NULL server-side; the sheet makes notes required so this is never blank.
        text: issueDetails!.notes,
        severity: UNSET_SEVERITY,
        isComplete: false,
        isSelfAssigned: false,
        assertionIdentifier: assertion.identifier,
        identifier: uuidv4(),
        jobId: this.job()!.id,
        lotAreaIdentifier,
        isConfirmedByHomeowner: false,
        createdAt: now,
        updatedAt: now,
        syncStatus: SyncableStatus.Pending
      };

      await this.createIssueForAssertion(issue, issueDetails!.media, null);
      return;
    }

    const isPass = response === ChecklistResponse.Pass;
    const changes: AssertionChanges = { isApplicable: isPass };

    if (this.isCcmInspection()) {
      changes.isCcmComplete = isPass;
      changes.ccmNote = "";
    } else {
      changes.isComplete = isPass;
      changes.note = "";
    }

    await this.updateAssertionState(assertion, changes);
  }

  async clearChecklistResponse(assertion: Assertion): Promise<void> {
    await this.assertionService.clearAssertionResponse(assertion, this.isCcmInspection());

    const updatedAt = new Date();
    this.assertions.update(assertions => (assertions ?? []).map(a => {
      if (a.id !== assertion.id) {
        return a;
      }

      const updated = { ...a, isApplicable: true, updatedAt };
      if (this.isCcmInspection()) {
        updated.isCcmComplete = false;
        updated.ccmNote = "";
      } else {
        updated.isComplete = false;
        updated.note = "";
      }

      return updated;
    }));
    this.media.update(mediaList => (mediaList ?? []).filter(m => m.parentIdentifier !== assertion.identifier));
  }

  async updateChecklistIssue(issue: AssertionIssue, notes: string, media: Media[], lotAreaIdentifier?: string): Promise<void> {
    const changes: Partial<Omit<AssertionIssue, "id">> = { text: notes, lotAreaIdentifier };
    const mediaFileIdentifierToIdMap = await this.assertionIssuesService.updateAssertionIssue(issue.id!, changes, media);

    const updatedAt = new Date();
    this.assertionIssues.update(issues => (issues ?? []).map(i =>
      (i.id === issue.id ? { ...i, text: notes, lotAreaIdentifier, updatedAt } : i)
    ));

    const newMedia = media.filter(m => !m.id).map(m => ({
      ...m,
      id: mediaFileIdentifierToIdMap[m.fileIdentifier],
      parentIdentifier: issue.identifier
    }));
    const deletedMediaIds = new Set(media.filter(m => m.deleted === 1 && m.id !== null && m.id !== undefined).map(m => m.id!));
    this.media.update(mediaList => [
      ...(mediaList ?? []).filter(m => !m.id || !deletedMediaIds.has(m.id)),
      ...newMedia
    ]);
  }
}
